import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Flex, VStack, Heading, Text, Button, Spinner, Image, Icon, Select,
  Badge, Card, CardHeader, CardBody,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
  useDisclosure, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import { batchAddTracksToPlaylist, batchRemoveTracksFromPlaylist, reorderPlaylistTracks, getStreamUrl, bulkUpdateTracks } from '../../api/libraryApi';
import type { LibraryTrackDto, BulkUpdateTrackField, BulkUpdateTracksBody } from '../../api/libraryApi';
import { LibraryBrowserPanel } from '../../components/LibraryBrowserPanel';
import { DraggableTrackList, type DraggableTrack } from '../../components/DraggableTrackList';
import { FloatingAudioPlayer } from '../../components/LibraryBrowserPanel/FloatingAudioPlayer';
import { BulkEditTracksModal } from '../library/components/BulkEditTracksModal';
import { ManagePlaylistsModal } from '../library/components/ManagePlaylistsModal';
import { PlaylistLicenseSuggestDialog } from './components/PlaylistLicenseSuggestDialog';
import { RelatedOverrideEditor } from '../playlist-collections/components/RelatedOverrideEditor';

interface CuratedTrack {
  id: string;
  trackId?: string;
  contentId: string;
  title: string;
  artist: string;
  durationSeconds: number | null;
  position: number;
  thumbnailUrl: string | null;
  genre: string | null;
  tags: string[];
}

interface PlaylistDetail {
  id: string;
  name: string;
  description: string | null;
  targetUseCase: string | null;
  thumbnailUrl: string | null;
  isPublished: boolean;
  isLicensed: boolean;
  isLicensedOverride: boolean | null;
  tracks: CuratedTrack[];
  trackCount: number;
  totalDurationSeconds: number;
  createdAt: string;
  updatedAt: string;
}

const cardShadow = '0px 18px 40px rgba(112, 144, 176, 0.12)';

function formatTotalDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function PlaylistDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  // The metadata-edit modal is shared between "Edit all tracks" (the whole playlist)
  // and the per-row edit button (a single track). `editContext === null` means closed.
  // sourceLabel is captured from the launch source (not inferred from count) so a
  // single-track *playlist* still reads "in this playlist".
  const [editContext, setEditContext] = useState<{ ids: string[]; sourceLabel?: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Bulk track-selection (by contentId) for the selection toolbar actions.
  const [selectedContentIds, setSelectedContentIds] = useState<Set<string>>(new Set());
  const [showManagePlaylists, setShowManagePlaylists] = useState(false);
  const [licenseSuggest, setLicenseSuggest] = useState<{ open: boolean; override: boolean | null }>({ open: false, override: null });
  const { isOpen: isRemoveSelectedOpen, onOpen: onRemoveSelectedOpen, onClose: onRemoveSelectedClose } = useDisclosure();
  const removeSelectedCancelRef = useRef<HTMLButtonElement>(null);

  // Floating player state
  const [playingTrack, setPlayingTrack] = useState<LibraryTrackDto | null>(null);
  const [playerStreamUrl, setPlayerStreamUrl] = useState<string | null>(null);
  const [playerSasToken, setPlayerSasToken] = useState<string | null>(null);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (!errorMessage) return;
    const timer = setTimeout(() => setErrorMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [errorMessage]);

  const { data: playlist, isLoading } = useQuery({
    queryKey: ['admin-playlist', id],
    queryFn: () => apiClient.get<PlaylistDetail>(`/admin/curated-playlists/${id}`),
    enabled: Boolean(id),
  });

  const removeTrack = useMutation({
    mutationFn: (trackId: string) => apiClient.delete(`/admin/curated-playlists/${id}/tracks/${trackId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-playlist', id] }),
  });

  // Bulk-remove the selected tracks from THIS playlist (by content id).
  const bulkRemoveSelected = useMutation({
    mutationFn: (contentIds: string[]) => batchRemoveTracksFromPlaylist(id!, contentIds),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-playlist', id] });
      setSelectedContentIds(new Set());
      onRemoveSelectedClose();
      toast({
        title: t('admin.playlists.removeSelected.done', { count: res.removedCount }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: () => {
      onRemoveSelectedClose();
      setErrorMessage(t('admin.playlists.removeSelected.failed', 'Failed to remove tracks'));
    },
  });

  const togglePublish = useMutation({
    mutationFn: () => apiClient.post(`/admin/curated-playlists/${id}/${playlist?.isPublished ? 'unpublish' : 'publish'}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-playlist', id] }),
  });

  // Admin override for the licensed indicator: true / false force it, null = auto-derive.
  // After a change, suggest applying a license to every track in the playlist.
  const setLicensedOverride = useMutation({
    mutationFn: (value: boolean | null) =>
      apiClient.put(`/admin/curated-playlists/${id}/licensed-override`, { isLicensedOverride: value }),
    onSuccess: (_data, value) => {
      queryClient.invalidateQueries({ queryKey: ['admin-playlist', id] });
      setLicenseSuggest({ open: true, override: value });
    },
  });

  const deletePlaylist = useMutation({
    mutationFn: () => apiClient.delete(`/admin/curated-playlists/${id}`),
    onSuccess: () => navigate('/curated-playlists'),
  });

  // Batch add tracks from drawer
  const batchAddMutation = useMutation({
    mutationFn: (contentIds: string[]) => batchAddTracksToPlaylist(id!, contentIds),
    onSuccess: (_data, contentIds) => {
      queryClient.invalidateQueries({ queryKey: ['admin-playlist', id] });
      toast({
        title: t('admin.playlists.tracksAdded', { count: contentIds.length, defaultValue: 'Added {{count}} tracks' }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: () => {
      setErrorMessage(t('admin.playlists.batchAddFailed', 'Failed to add tracks'));
    },
  });

  const handleTracksSelected = useCallback((trackIds: string[]) => {
    batchAddMutation.mutate(trackIds);
  }, [batchAddMutation]);

  // Bulk-edit metadata for every track in this playlist (FR-076–FR-078). The edit
  // applies to each track everywhere it is used, so refresh the playlist + library.
  const bulkEditMutation = useMutation({
    mutationFn: ({
      trackIds,
      fields,
      values,
    }: {
      trackIds: string[];
      fields: BulkUpdateTrackField[];
      values: Omit<BulkUpdateTracksBody, 'trackIds' | 'fields'>;
    }) => bulkUpdateTracks(trackIds, fields, values),
    onMutate: ({ trackIds }) => {
      toast({
        title: t('admin.library.bulkEdit.updating', { count: trackIds.length }),
        status: 'loading',
        duration: null,
        id: 'playlist-bulk-edit-progress',
      });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-playlist', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-library'] });
      queryClient.invalidateQueries({ queryKey: ['admin-library-filter-options'] });
      toast.close('playlist-bulk-edit-progress');
      toast({
        title: t('admin.library.bulkEdit.updated', { count: res.updatedCount }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setEditContext(null);
    },
    onError: () => {
      toast.close('playlist-bulk-edit-progress');
      toast({
        title: t('admin.library.bulkEdit.updateFailed'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    },
  });

  // Drag-and-drop reorder
  const reorderMutation = useMutation({
    mutationFn: (trackIds: string[]) => reorderPlaylistTracks(id!, trackIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-playlist', id] });
    },
    onError: () => {
      setErrorMessage(t('admin.playlists.reorderFailed', 'Failed to reorder tracks'));
      queryClient.invalidateQueries({ queryKey: ['admin-playlist', id] });
    },
  });

  const handleReorder = useCallback((reorderedTracks: DraggableTrack[]) => {
    queryClient.setQueryData(['admin-playlist', id], (old: PlaylistDetail | undefined) => {
      if (!old) return old;
      return {
        ...old,
        tracks: reorderedTracks.map((t) => ({
          id: t.id,
          trackId: t.trackId,
          contentId: t.contentId,
          title: t.title,
          artist: t.artist,
          durationSeconds: t.durationSeconds,
          position: t.position,
          thumbnailUrl: t.thumbnailUrl,
          genre: t.genre,
          tags: t.tags,
        })),
      };
    });
    reorderMutation.mutate(reorderedTracks.map((t) => t.id));
  }, [id, queryClient, reorderMutation]);

  const handleRemoveTrack = useCallback((trackId: string) => {
    removeTrack.mutate(trackId);
  }, [removeTrack]);

  // Floating player toggle
  const handlePlayToggle = useCallback(async (track: DraggableTrack) => {
    if (playingTrack?.id === track.contentId) {
      setPlayingTrack(null);
      setPlayerStreamUrl(null);
      setPlayerSasToken(null);
      return;
    }
    try {
      const result = await getStreamUrl(track.contentId);
      setPlayingTrack({
        id: track.contentId,
        title: track.title ?? '',
        artist: track.artist ?? '',
        album: null,
        thumbnailUrl: track.thumbnailUrl,
        genre: track.genre,
        mood: null,
        tempo: null,
        durationSeconds: track.durationSeconds ?? 0,
        processingStatus: 'completed',
        status: 'active',
        fileUrl: null,
        processingError: null,
        streamRelativePath: null,
        tags: track.tags,
        createdAt: '',
        updatedAt: '',
        licenseId: null,
        licenseName: null,
        rightsHolder: null,
        rightsTerritory: null,
        contract: null,
        rightsHolderTrackId: null,
        rightsValidFrom: null,
        rightsValidTo: null,
      });
      setPlayerStreamUrl(result.streamUrl);
      setPlayerSasToken(result.sasToken);
    } catch {
      setPlayingTrack(null);
    }
  }, [playingTrack?.id]);

  const handleClosePlayer = useCallback(() => {
    setPlayingTrack(null);
    setPlayerStreamUrl(null);
    setPlayerSasToken(null);
  }, []);

  // Map playlist tracks to DraggableTrack format
  const draggableTracks: DraggableTrack[] = useMemo(() =>
    (playlist?.tracks ?? []).map((t) => ({
      id: t.id,
      trackId: t.trackId ?? t.id,
      contentId: t.contentId,
      title: t.title,
      artist: t.artist,
      durationSeconds: t.durationSeconds,
      position: t.position,
      thumbnailUrl: t.thumbnailUrl,
      genre: t.genre,
      tags: t.tags ?? [],
    })),
    [playlist?.tracks]
  );

  // Track content IDs in the playlist — reused both to exclude already-added
  // tracks from the add-drawer and to target the "Edit all tracks" bulk edit.
  const playlistTrackIds = useMemo(() =>
    draggableTracks.map((t) => t.contentId),
    [draggableTracks]
  );
  const excludeTrackIds = playlistTrackIds;

  // Drop any selected ids that are no longer in the playlist (removed/reordered away).
  useEffect(() => {
    setSelectedContentIds((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(playlistTrackIds);
      const next = new Set([...prev].filter((cid) => present.has(cid)));
      return next.size === prev.size ? prev : next;
    });
  }, [playlistTrackIds]);

  const selectedIdList = useMemo(
    () => playlistTrackIds.filter((cid) => selectedContentIds.has(cid)),
    [playlistTrackIds, selectedContentIds],
  );

  const handleToggleSelect = useCallback((track: DraggableTrack) => {
    setSelectedContentIds((prev) => {
      const next = new Set(prev);
      if (next.has(track.contentId)) next.delete(track.contentId);
      else next.add(track.contentId);
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback((selectAll: boolean) => {
    setSelectedContentIds(selectAll ? new Set(playlistTrackIds) : new Set());
  }, [playlistTrackIds]);

  const handleApplyBulkEdit = useCallback(
    (fields: BulkUpdateTrackField[], values: Omit<BulkUpdateTracksBody, 'trackIds' | 'fields'>) => {
      if (!editContext || editContext.ids.length === 0) return;
      bulkEditMutation.mutate({ trackIds: editContext.ids, fields, values });
    },
    [editContext, bulkEditMutation],
  );

  const handleEditSingleTrack = useCallback(
    (track: DraggableTrack) => setEditContext({ ids: [track.contentId] }),
    [],
  );

  const totalDuration = playlist?.totalDurationSeconds ??
    draggableTracks.reduce((sum, t) => sum + (t.durationSeconds ?? 0), 0);

  if (isLoading) return <Flex justify="center" p={8}><Spinner size="xl" /></Flex>;
  if (!playlist) return <Text>{t('admin.common.noResults')}</Text>;

  return (
    <VStack spacing={5} align="stretch" pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="playlist-detail-page">
      {/* ── Header Card ── */}
      <Card borderRadius="20px" boxShadow={cardShadow}>
        <CardBody>
          <Flex gap={5} align="flex-start" direction={{ base: 'column', sm: 'row' }}>
            {/* Cover art */}
            <Box
              w="120px"
              h="120px"
              flexShrink={0}
              borderRadius="16px"
              overflow="hidden"
              bg="gray.100"
            >
              {playlist.thumbnailUrl ? (
                <Image
                  src={playlist.thumbnailUrl}
                  alt={playlist.name}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  data-testid="playlist-cover-image"
                />
              ) : (
                <Flex w="100%" h="100%" align="center" justify="center" bg="gray.200">
                  <Icon viewBox="0 0 24 24" boxSize={10} color="gray.400">
                    <path fill="currentColor" d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6Z" />
                  </Icon>
                </Flex>
              )}
            </Box>

            {/* Playlist identity + stats */}
            <VStack spacing={3} flex={1} align="stretch">
              <Heading as="h1" size="lg" noOfLines={1}>{playlist.name}</Heading>
              {playlist.description && (
                <Text color="gray.600" fontSize="md" noOfLines={2}>{playlist.description}</Text>
              )}
              <Flex gap={3} flexWrap="wrap" align="center">
                <Badge
                  colorScheme={playlist.isPublished ? 'green' : 'gray'}
                  fontSize="xs"
                  px={2}
                  py={0.5}
                  borderRadius="md"
                >
                  {playlist.isPublished ? t('admin.playlists.published') : t('admin.playlists.draft')}
                </Badge>
                {playlist.isLicensed && (
                  <Badge
                    colorScheme="purple"
                    fontSize="xs"
                    px={2}
                    py={0.5}
                    borderRadius="md"
                    data-testid="playlist-licensed-badge"
                  >
                    {t('admin.library.licensedBadge')}
                  </Badge>
                )}
                <Text fontSize="sm" color="gray.500">
                  {draggableTracks.length} {t('admin.playlists.tracks')} · {formatTotalDuration(totalDuration)}
                </Text>
                {playlist.targetUseCase && (
                  <Text fontSize="sm" color="gray.500">{playlist.targetUseCase}</Text>
                )}
                <Text fontSize="sm" color="gray.400">
                  {t('admin.playlists.createdAt', 'Created')} {new Date(playlist.createdAt).toLocaleDateString()}
                </Text>
              </Flex>

              {/* Action buttons */}
              <Flex gap={2} flexWrap="wrap" mt={1}>
                <Button size="sm" variant="outline" onClick={() => navigate(`/curated-playlists/${id}/edit`)} data-testid="btn-edit">
                  {t('common.edit')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme={playlist.isPublished ? 'orange' : 'green'}
                  onClick={() => togglePublish.mutate()}
                  isDisabled={togglePublish.isPending}
                  data-testid="btn-toggle-publish"
                >
                  {playlist.isPublished ? t('admin.playlists.unpublish') : t('admin.playlists.publish')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={() => deletePlaylist.mutate()}
                  isDisabled={deletePlaylist.isPending}
                  data-testid="btn-delete"
                >
                  {t('common.delete')}
                </Button>

                {/* Licensed override — styled as a button-height bordered block, pushed to the right */}
                <Flex
                  align="center"
                  gap={2}
                  ml="auto"
                  h="32px"
                  pl={3}
                  pr={1}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="12px"
                  data-testid="playlist-licensed-override"
                >
                  <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">{t('admin.playlists.licensed.label')}</Text>
                  <Select
                    size="sm"
                    variant="unstyled"
                    w="auto"
                    minW="90px"
                    fontWeight="500"
                    value={
                      playlist.isLicensedOverride === true ? 'licensed'
                        : playlist.isLicensedOverride === false ? 'unlicensed'
                        : 'auto'
                    }
                    onChange={(e) => {
                      const v = e.target.value === 'licensed' ? true : e.target.value === 'unlicensed' ? false : null;
                      setLicensedOverride.mutate(v);
                    }}
                    isDisabled={setLicensedOverride.isPending}
                    data-testid="playlist-licensed-override-select"
                  >
                    <option value="auto">{t('admin.playlists.licensed.auto')}</option>
                    <option value="licensed">{t('admin.playlists.licensed.licensed')}</option>
                    <option value="unlicensed">{t('admin.playlists.licensed.unlicensed')}</option>
                  </Select>
                </Flex>
              </Flex>
            </VStack>
          </Flex>
        </CardBody>
      </Card>

      {/* ── Tracks + Related tabs ── */}
      <Tabs variant="soft-rounded" colorScheme="brand" isLazy>
        <TabList mb={3}>
          <Tab data-testid="playlist-tab-tracks">{t('admin.playlists.tracks', 'Tracks')}</Tab>
          <Tab data-testid="playlist-tab-related">{t('admin.relatedOverride.tab', 'Related')}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
      {/* ── Tracks Card ── */}
      <Card borderRadius="20px" boxShadow={cardShadow}>
        <CardHeader pb={0}>
          <Flex justify="space-between" align="center">
            <Flex align="center" gap={2}>
              <Heading as="h2" size="md">{t('admin.playlists.tracks')}</Heading>
              <Badge colorScheme="gray" borderRadius="full" fontSize="xs">
                {draggableTracks.length}
              </Badge>
            </Flex>
            <Flex gap={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditContext({ ids: playlistTrackIds, sourceLabel: 'playlist' })}
                isDisabled={draggableTracks.length === 0}
                data-testid="playlist-edit-all-tracks-button"
              >
                {t('admin.library.bulkEdit.editAllTracks', 'Edit all tracks')}
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={onDrawerOpen}
                data-testid="playlist-add-tracks-button"
              >
                {t('admin.playlists.addTracks', 'Add Tracks')}
              </Button>
            </Flex>
          </Flex>
        </CardHeader>
        {/* Bulk-selection toolbar — appears once one or more tracks are selected */}
        {selectedIdList.length > 0 && (
          <Box px={6} pt={3}>
            <Flex
              align="center"
              gap={3}
              flexWrap="wrap"
              bg="blue.50"
              borderRadius="10px"
              px={4}
              py={2}
              data-testid="playlist-selection-toolbar"
            >
              <Badge colorScheme="blue" borderRadius="full" data-testid="playlist-selection-count">
                {t('admin.library.bulkEdit.selectedCount', { count: selectedIdList.length })}
              </Badge>
              <Button size="sm" variant="ghost" onClick={() => setSelectedContentIds(new Set())} data-testid="playlist-selection-clear">
                {t('admin.playlists.clearSelection', 'Clear')}
              </Button>
              <Box flex={1} />
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditContext({ ids: selectedIdList, sourceLabel: 'playlist' })}
                data-testid="playlist-edit-selected-button"
              >
                {t('admin.library.bulkEdit.editMetadata')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                onClick={onRemoveSelectedOpen}
                data-testid="playlist-remove-selected-button"
              >
                {t('admin.playlists.removeSelected.action')}
              </Button>
              <Button
                size="sm"
                colorScheme="brand"
                onClick={() => setShowManagePlaylists(true)}
                data-testid="playlist-manage-selected-button"
              >
                {t('admin.playlists.manage.action')}
              </Button>
            </Flex>
          </Box>
        )}
        <CardBody px={0} pt={4}>
          <DraggableTrackList
            tracks={draggableTracks}
            onReorder={handleReorder}
            onRemoveTrack={handleRemoveTrack}
            playingTrackId={playingTrack?.id}
            onPlayToggle={handlePlayToggle}
            onEditTrack={handleEditSingleTrack}
            selectable
            selectedIds={selectedContentIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
          />
        </CardBody>
      </Card>
          </TabPanel>
          <TabPanel px={0}>
            {id && (
              <Card borderRadius="20px" boxShadow={cardShadow}>
                <CardBody>
                  <RelatedOverrideEditor playlistId={id} />
                </CardBody>
              </Card>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Spacer for floating player */}
      {playingTrack && <Box h="80px" />}

      {/* ── Floating Audio Player ── */}
      {playingTrack && playerStreamUrl && (
        <FloatingAudioPlayer
          track={playingTrack}
          streamUrl={playerStreamUrl}
          sasToken={playerSasToken}
          onClose={handleClosePlayer}
        />
      )}

      {/* ── Add Tracks Drawer ── */}
      <Drawer isOpen={isDrawerOpen} placement="right" size="lg" onClose={onDrawerClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            {t('admin.playlists.addTracks', 'Add Tracks')}
          </DrawerHeader>
          <DrawerBody pt={4}>
            <LibraryBrowserPanel
              mode="selector"
              onTracksSelected={handleTracksSelected}
              excludeTrackIds={excludeTrackIds}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* ── Metadata edit: all tracks (Edit all) or a single track (per-row edit) ── */}
      {editContext !== null && (
        <BulkEditTracksModal
          open
          trackIds={editContext.ids}
          onClose={() => setEditContext(null)}
          onApply={handleApplyBulkEdit}
          isApplying={bulkEditMutation.isPending}
          sourceLabel={editContext.sourceLabel}
        />
      )}

      {/* ── Confirm bulk remove of selected tracks from this playlist ── */}
      <AlertDialog
        isOpen={isRemoveSelectedOpen}
        leastDestructiveRef={removeSelectedCancelRef}
        onClose={onRemoveSelectedClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent data-testid="playlist-remove-selected-dialog">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('admin.playlists.removeSelected.confirmTitle')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('admin.playlists.removeSelected.confirmBody', { count: selectedIdList.length })}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={removeSelectedCancelRef} onClick={onRemoveSelectedClose} data-testid="playlist-remove-selected-cancel">
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={() => bulkRemoveSelected.mutate(selectedIdList)}
                isLoading={bulkRemoveSelected.isPending}
                data-testid="playlist-remove-selected-confirm"
              >
                {t('admin.playlists.removeSelected.action')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* ── Manage playlists for the selected tracks (add to / remove from curated playlists) ── */}
      {showManagePlaylists && (
        <ManagePlaylistsModal
          open={showManagePlaylists}
          trackIds={selectedIdList}
          sourceLabel="playlist"
          onClose={() => setShowManagePlaylists(false)}
          onDone={() => {
            queryClient.invalidateQueries({ queryKey: ['admin-playlist', id] });
            setSelectedContentIds(new Set());
            toast({ title: t('admin.playlists.manage.done'), status: 'success', duration: 3000, isClosable: true });
          }}
        />
      )}

      {/* ── After a licensed-override change, suggest applying a license to all tracks ── */}
      {licenseSuggest.open && id && playlistTrackIds.length > 0 && (
        <PlaylistLicenseSuggestDialog
          open={licenseSuggest.open}
          playlistId={id}
          trackIds={playlistTrackIds}
          overrideValue={licenseSuggest.override}
          onClose={() => setLicenseSuggest({ open: false, override: null })}
        />
      )}
    </VStack>
  );
}
